import { useRef, useState } from "react";
import { Paperclip, Trash2, Download, Plus, ChevronDown, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib_app/api";

export const DOC_CATEGORIES = [
  { key: "faktur_pajak", label: "Faktur Pajak", ppnOnly: true, tint: "bg-[#E4F9EC] text-[#3f7a52] border-[#B7E7C7]" },
  { key: "scan_invoice", label: "Scan Invoice TTD", tint: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]" },
  { key: "bukti_transfer", label: "Bukti Transfer", tint: "bg-[#E6E2F4] text-[#4A3B77] border-[#CFC7EA]" },
  { key: "lainnya", label: "Lainnya", tint: "bg-[#F4F5F7] text-[#626061] border-[#DCE0E7]" },
];

const catMeta = (key) => DOC_CATEGORIES.find((c) => c.key === key) || DOC_CATEGORIES[3];

const fileTint = (name) => {
  const ext = (name || "").split(".").pop()?.toLowerCase();
  if (ext === "pdf") return { label: "PDF", bg: "bg-[#FDE7E9]", fg: "text-[#CE6968]" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { label: "XLS", bg: "bg-[#E4F9EC]", fg: "text-[#3EB876]" };
  if (["doc", "docx"].includes(ext)) return { label: "DOC", bg: "bg-[#DDEBFB]", fg: "text-[#2F4B70]" };
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return { label: "IMG", bg: "bg-[#E6E2F4]", fg: "text-[#4A3B77]" };
  return { label: "FILE", bg: "bg-[#F4F5F7]", fg: "text-[#626061]" };
};

/** Shared invoice document board — used in the invoice modal and the project modal.
 * Visually aligned with ProjectAttachmentsBoard (filter chips + collapsible upload + grouped list). */
export default function InvoiceDocs({
  attachments = [], invoices = [], fixedInvoiceId = null, canEdit = false, canDelete = null,
  onUpload, onDelete, onOpen, showInvoiceLabel = false, testid = "invoice-docs",
}) {
  const pickable = (fixedInvoiceId ? invoices.filter((i) => i.id === fixedInvoiceId) : invoices).filter((i) => i.status !== "void");
  const [invoiceId, setInvoiceId] = useState(fixedInvoiceId || pickable[0]?.id || "");
  const [category, setCategory] = useState("scan_invoice");
  const [filter, setFilter] = useState("all");
  const [panelOpen, setPanelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const activeInvoice = pickable.find((i) => i.id === (fixedInvoiceId || invoiceId));
  const cats = DOC_CATEGORIES.filter((c) => !c.ppnOnly || activeInvoice?.ppn_type === "ppn");
  const activeCategory = cats.some((c) => c.key === category) ? category : (cats[0]?.key || "lainnya");

  const handleFiles = async (files) => {
    if (!files?.length) return;
    const target = fixedInvoiceId || invoiceId;
    if (!target) return;
    setBusy(true);
    try { await onUpload?.(target, activeCategory, files); } finally { setBusy(false); }
  };

  const counts = {
    all: attachments.length,
    ...Object.fromEntries(DOC_CATEGORIES.map((c) => [c.key, attachments.filter((a) => a.category === c.key).length])),
  };
  const visibleGroups = DOC_CATEGORIES
    .filter((c) => filter === "all" || filter === c.key)
    .map((c) => ({ cat: c, items: attachments.filter((a) => a.category === c.key) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-3" data-testid={testid}>
      {/* HEADER: filter chips + upload */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          {[["all", "Semua"], ...DOC_CATEGORIES.map((c) => [c.key, c.label])].map(([key, label]) => (
            <button
              key={key} type="button" onClick={() => setFilter(key)}
              data-testid={`${testid}-filter-${key}`}
              className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-all inline-flex items-center gap-1.5 ${
                filter === key ? "bg-[#090808] text-white border-[#090808]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"
              }`}
            >
              {label}
              <span className={`font-num tabular-nums ${filter === key ? "text-white/70" : "text-[#A0A3A8]"}`}>{counts[key]}</span>
            </button>
          ))}
        </div>
        {canEdit && pickable.length > 0 && (
          <button
            type="button" onClick={() => setPanelOpen((v) => !v)} data-testid={`${testid}-upload-toggle`}
            className="h-8 px-3.5 rounded-full bg-gradient-to-r from-[#6B8FE2] to-[#3B4A63] text-white text-[11px] font-semibold inline-flex items-center gap-1.5 shrink-0"
          >
            <Plus className="w-3.5 h-3.5" /> Upload
            <ChevronDown className={`w-3 h-3 transition-transform ${panelOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>

      {/* UPLOAD PANEL */}
      {canEdit && panelOpen && pickable.length > 0 && (
        <div className="rounded-2xl border border-[#B0C7EA]/70 bg-[#F5F9FF]/70 p-4 space-y-3" data-testid={`${testid}-upload-panel`}>
          <div className={`grid gap-3 ${fixedInvoiceId ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
            {!fixedInvoiceId && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63] mb-1.5">Invoice</div>
                <Select value={invoiceId} onValueChange={setInvoiceId}>
                  <SelectTrigger data-testid={`${testid}-invoice-select`} className="h-10 rounded-xl bg-white border-[#DCE0E7]"><SelectValue placeholder="Pilih invoice" /></SelectTrigger>
                  <SelectContent>
                    {pickable.map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        <span className="font-num text-xs">{i.number}</span>
                        <span className="text-[10px] text-[#A0A3A8] ml-2">{i.ppn_type === "ppn" ? "PPN" : "Non-PPN"}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63] mb-1.5">Jenis Dokumen</div>
              <div className="flex flex-wrap items-center gap-1.5">
                {cats.map((c) => (
                  <button
                    key={c.key} type="button" onClick={() => setCategory(c.key)}
                    data-testid={`${testid}-cat-${c.key}`}
                    className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-all ${
                      activeCategory === c.key ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <input
            ref={inputRef} type="file" multiple className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
            data-testid={`${testid}-file-input`}
          />
          <button
            type="button" onClick={() => inputRef.current?.click()} disabled={busy || !(fixedInvoiceId || invoiceId)}
            data-testid={`${testid}-upload-btn`}
            className="w-full h-11 rounded-full bg-[#090808] text-white text-sm font-semibold hover:bg-black inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Paperclip className="w-4 h-4" /> {busy ? "Mengupload…" : `Pilih file — ${catMeta(activeCategory).label}`}
          </button>
          <div className="text-[10px] text-[#A0A3A8] text-center">PDF · DOC · XLS · Gambar · maks 15MB per file</div>
        </div>
      )}

      {/* LIST */}
      {visibleGroups.length === 0 ? (
        <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]" data-testid={`${testid}-empty`}>
          Belum ada dokumen pada kategori ini.
        </div>
      ) : (
        <div className="rounded-2xl border border-[#DCE0E7]/70 bg-white overflow-hidden">
          {visibleGroups.map((g) => (
            <div key={g.cat.key} data-testid={`${testid}-group-${g.cat.key}`}>
              <div className="px-4 py-2 bg-[#F7F8FA] border-y border-[#DCE0E7]/60 first:border-t-0 flex items-center gap-2">
                <span className="text-[9px] uppercase tracking-widest font-bold text-[#626061]">{g.cat.label}</span>
                <span className="text-[9px] font-num tabular-nums text-[#A0A3A8]">{g.items.length}</span>
              </div>
              <div className="divide-y divide-[#DCE0E7]/60">
                {g.items.map((a) => {
                  const ic = fileTint(a.filename);
                  return (
                    <div key={a.id} data-testid={`${testid}-item-${a.id}`} className="px-4 py-3 flex items-center gap-3 hover:bg-[#FAFBFC] transition">
                      <div className={`w-10 h-10 rounded-xl ${ic.bg} ${ic.fg} flex items-center justify-center font-bold text-[10px] shrink-0`}>{ic.label}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-[#090808] truncate">{a.filename}</div>
                        <div className="text-[11px] text-[#A0A3A8] mt-0.5 flex items-center gap-1.5 flex-wrap">
                          {showInvoiceLabel && a.invoice_number && (
                            <span className="inline-flex items-center h-4 px-1.5 rounded-full text-[8px] font-bold uppercase tracking-widest bg-[#F4F5F7] text-[#626061] border border-[#DCE0E7] font-num">{a.invoice_number}</span>
                          )}
                          {a.uploaded_by_name && <span className="truncate">{a.uploaded_by_name}</span>}
                          {a.uploaded_at && <span className="font-num tabular-nums">{fmtDate(a.uploaded_at)}</span>}
                          {a.size ? <span className="font-num tabular-nums">{Math.max(1, Math.round(a.size / 1024))} KB</span> : null}
                        </div>
                      </div>
                      <button onClick={() => onOpen?.(a)} title="Buka / Unduh" data-testid={`${testid}-open-${a.id}`} className="w-8 h-8 rounded-full hover:bg-[#F4F5F7] text-[#3B4A63] flex items-center justify-center shrink-0">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      {(canDelete ?? canEdit) && (
                        <button onClick={() => onDelete?.(a)} title="Hapus" data-testid={`${testid}-delete-${a.id}`} className="w-8 h-8 rounded-full hover:bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {canEdit && pickable.length === 0 && (
        <div className="rounded-2xl bg-[#FFF7E8] border border-[#F2D9A0] p-4 text-[12px] text-[#8C6318] flex items-center gap-2">
          <FileText className="w-4 h-4 shrink-0" /> Buat invoice untuk proyek ini terlebih dahulu agar dokumen bisa dilampirkan.
        </div>
      )}
    </div>
  );
}
